<script lang="ts" setup>
import Vue, { onMounted, ref, onBeforeUnmount, watch, nextTick } from 'vue';
import Suggest from '@/corePagesModule/components/Suggest.vue';
import SvgLoader from '@/utils/SvgLoader.vue';

const props = defineProps<{
  suggestions: string[]; // プルダウンで表示する値
  selectedId?: string; // 選択されている値
  value?: string; // 表示する値
  defaultValue?: string; // 初期値
  hint?: string; // ヒントテキスト
  prefix?: string; // 先頭に何かつけたい時に使う
  suffix?: string; // 末尾に何かつけたい時に使う
}>();

const result = ref<DOMRect>();
const xLength = ref(0);
const yLength = ref(0);
const isOpen = ref(false);

// 自分のルート要素と、Teleport先のラッパー要素
const rootEl = ref<HTMLElement | null>(null);
const portalEl = ref<HTMLElement | null>(null);

// 直前の“開いたクリック”を無視するためのフラグ
let ignoreNextDocumentPointerDown = false;

/**
 * ドキュメント外クリックハンドラ
 * Teleport先(portalEl)と自分(rootEl)を両方内側扱いにする
 * @param e - PointerEvent
 */
const onDocumentPointerDown = (e: PointerEvent) => {
  if (!isOpen.value) return;
  if (ignoreNextDocumentPointerDown) return;

  const path = (e.composedPath && e.composedPath()) || [];
  const clickedInsideRoot = rootEl.value ? path.includes(rootEl.value) : false;
  const clickedInsidePortal = portalEl.value ? path.includes(portalEl.value) : false;

  if (clickedInsideRoot || clickedInsidePortal) return;

  // どちらでもなければ外側クリック → 閉じる
  isOpen.value = false;
};

/**
 * ドキュメントのポインターイベントを登録
 */
const addDocListener = () => {
  // “開いたクリック”と同一イベントで即発火しないよう、次フレームで登録
  ignoreNextDocumentPointerDown = true;
  setTimeout(() => {
    document.addEventListener('pointerdown', onDocumentPointerDown, true);
    // 1フレームだけ無視すれば十分
    ignoreNextDocumentPointerDown = false;
  }, 0);
};

/**
 * ドキュメントのポインターイベントを削除
 */
const removeDocListener = () => {
  document.removeEventListener('pointerdown', onDocumentPointerDown, true);
};

const emits = defineEmits<{
  (e: 'update-value', value: string): void;
  (e: 'update-selected-id', value: string): void;
}>();

/**
 * クリックされたらinputを表示してフォーカスする
 */
const onClickLabel = async () => {
  await nextTick();
  document.getElementById('selectable-input-container__input')?.focus();

  const element = document.getElementById(selectableInputContainerId.value);
  if (!element) return;

  // 位置計算
  result.value = element.getBoundingClientRect();
  xLength.value = result.value.left;
  yLength.value = result.value.bottom;

  // 開閉トグル
  const nextState = !isOpen.value;
  isOpen.value = !isOpen.value;

  // 選択されているIDを更新
  if (nextState) {
    emits('update-selected-id', selectableInputContainerId.value);
    // Teleport描画が反映されたあとに portalEl を掴んで外クリック監視を開始
    await nextTick();
    addDocListener();
  } else {
    removeDocListener();
  }
};

/**
 * サジェストが選択された時の処理
 * @param suggest - 選択されたサジェスト
 */
const onClickSuggest = (suggest: string) => {
  isOpen.value = false;
  removeDocListener();
  emits('update-value', suggest);
};

// ランダムID生成
const selectableInputContainerId = ref<string>('');

onMounted(() => {
  selectableInputContainerId.value = Math.random().toString(32).substring(2);
});

onBeforeUnmount(() => {
  removeDocListener();
});

watch(
  () => props.selectedId,
  (newValue) => {
    if (newValue !== selectableInputContainerId.value) {
      isOpen.value = false;
      removeDocListener();
    }
  }
);
</script>

<template>
  <div :id="selectableInputContainerId" ref="rootEl" class="selectable-input-container">
    <div class="label-container rounded-base-small" @click="onClickLabel">
      <div
        class="label-container__text selectable-input-container__text-box selectable-input-container__text-box--label"
        :class="{ 'label-container--content': value, 'label-container--hint': !value }"
      >
        {{ value ?? hint }}
      </div>
      <SvgLoader class="label-container__icon" :name="'arrowDown'" />
    </div>

    <Teleport v-if="suggestions.length > 0 && isOpen" to="body">
      <div ref="portalEl">
        <Suggest
          class="selectable-input-container__suggests rounded-base-small"
          :style="{
            top: `${yLength}px`,
            left: `${xLength}px`,
          }"
          :options="suggestions"
          :min-width="`${result?.width}px`"
          @click-suggest="onClickSuggest"
        />
      </div>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
.selectable-input-container {
  display: inline-block;
  position: relative;
  cursor: pointer;
  z-index: 11;
  &__text-box {
    &--input {
      width: 100%;
    }
  }
  &__suggests {
    position: absolute;
    top: 100%;
    overflow-y: scroll;
    background-color: $semanticWhite;
    border: 1px solid $borderDefault;
    z-index: 1050;
  }
}

.label-container {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  background-color: $semanticWhite;

  &--content {
    color: $textDefault;
  }

  &--hint {
    color: $textSecondary;
  }

  &__icon {
    height: 24px;
    width: 24px;
    padding-top: 8.59px; //top指定できないので、代わり
    padding-left: 6px; //left指定できないので、代わり
  }
}
</style>
